import { useEffect, useMemo, useState } from 'react';
import type { ThemeTokens } from '@digiwedge/cmp-registry-types';
import type { CmpThemeTokens } from '@digiwedge/cmp-consent-react';

export type ThemeState = {
  tokens: CmpThemeTokens;
  version: number;
  loading: boolean;
  error?: string;
};

export function toCmpThemeTokens(tokens?: ThemeTokens | null): CmpThemeTokens {
  if (!tokens) return {};
  return {
    bg: tokens.cmpBg,
    text: tokens.cmpText,
    border: tokens.cmpBorder,
    backdrop: tokens.cmpBackdrop,
    shadow: tokens.cmpShadow,
    radius: tokens.cmpRadius,
    btnPrimaryBg: tokens.btnPrimaryBg,
    btnPrimaryText: tokens.btnPrimaryText,
    btnPrimaryHoverBg: tokens.btnPrimaryHoverBg,
    btnSecondaryBg: tokens.btnSecondaryBg,
    btnSecondaryText: tokens.btnSecondaryText,
    btnSecondaryBorder: tokens.btnSecondaryBorder,
  };
}

function normalizeBaseUrl(baseUrl: string | undefined | null): string | null {
  if (!baseUrl) return null;
  const trimmed = baseUrl.trim();
  if (!trimmed) return null;
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

export function useThemeTokens(
  registryUrl: string | undefined,
  siteKey: string | undefined | null,
): ThemeState {
  const [state, setState] = useState<ThemeState>({ tokens: {}, version: 0, loading: true });

  useEffect(() => {
    let cancelled = false;
    const base = normalizeBaseUrl(registryUrl);
    if (!siteKey || !base) {
      setState({ tokens: {}, version: 0, loading: false });
      return () => {
        cancelled = true;
      };
    }
    const abort = new AbortController();
    async function fetchTheme() {
      setState((prev) => ({ ...prev, loading: true, error: undefined }));
      try {
        const url = `${base}/v1/config?site_key=${encodeURIComponent(siteKey)}&v=live`;
        const res = await fetch(url, {
          credentials: 'include',
          signal: abort.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`config ${res.status}`);
        const json = (await res.json()) as { themeTokens?: ThemeTokens; themeVersion?: number };
        if (cancelled) return;
        setState({
          tokens: toCmpThemeTokens(json.themeTokens),
          version: json.themeVersion ?? 0,
          loading: false,
        });
      } catch (err) {
        if (cancelled || abort.signal.aborted) return;
        setState({ tokens: {}, version: 0, loading: false, error: (err as Error).message });
      }
    }
    fetchTheme().catch(() => undefined);
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [registryUrl, siteKey]);

  const tokens = useMemo(() => state.tokens, [state.tokens]);

  return { ...state, tokens };
}
